type CachedResponseEntry = {
  response: any | null;
  fetchedAt: number;
  promise?: Promise<any | null>;
};

export type ResponsePhotoFetcher = () => Promise<any | null>;

export const extractPhotosFromResponse = (response: any, questionId?: string): any[] => {
  if (!response) {
    return [];
  }

  let photos: any[] = Array.isArray(response.photos) ? response.photos : [];

  if (questionId && response.formData && response.formData[questionId]?.photos) {
    photos = response.formData[questionId].photos;
  }

  if (questionId) {
    photos = photos.filter(
      (photo) =>
        photo.questionId === questionId ||
        photo.metadata?.questionId === questionId
    );
  }

  return photos || [];
};

export class ReviewResponsePhotoCache {
  private readonly entries = new Map<number, CachedResponseEntry>();

  constructor(private readonly ttlMs = 30 * 1000) {}

  invalidate(responseId: number): void {
    this.entries.delete(responseId);
  }

  async getPhotos(
    responseId: number,
    questionId: string | undefined,
    fetchResponse: ResponsePhotoFetcher
  ): Promise<any[]> {
    const cacheEntry = this.entries.get(responseId);
    const now = Date.now();

    if (cacheEntry?.response && now - cacheEntry.fetchedAt < this.ttlMs) {
      return extractPhotosFromResponse(cacheEntry.response, questionId);
    }

    if (cacheEntry?.promise) {
      const cachedResponse = await cacheEntry.promise;
      return extractPhotosFromResponse(cachedResponse, questionId);
    }

    const fetchPromise = fetchResponse()
      .then((response) => {
        this.entries.set(responseId, {
          response,
          fetchedAt: Date.now(),
        });
        return response;
      })
      .finally(() => {
        const entry = this.entries.get(responseId);
        if (entry) {
          delete entry.promise;
        }
      });

    this.entries.set(responseId, {
      response: cacheEntry?.response ?? null,
      fetchedAt: cacheEntry?.fetchedAt ?? 0,
      promise: fetchPromise,
    });

    const response = await fetchPromise;
    return extractPhotosFromResponse(response, questionId);
  }
}
